/**
 * Text Field module
 *
 * This module provides advanced text field functionality including embeddable
 * fields (location custom fields) and the use of a text asset in place of
 * a field
 *
 * @author MattJS (matt@yext.com)
 */
goog.provide('yext.textfield');
goog.provide('yext.textfield.TextField');
goog.provide('yext.textfield.events');

goog.require('yext');
goog.require('yext.dam.assetselector.events');
goog.require('yext.dam.assetselector.types');
goog.require('yext.dam.assetselector.usage');
/** @suppress {extraRequire} */
goog.require('yext.plugins.contextualHelp');
/** @suppress {extraRequire} */
goog.require('yext.plugins.inputchange');
goog.require('yext.textfield.asset');
goog.require('yext.textfield.embed');
goog.require('yext.location.helpers');

// Maps TextField objects to elements
yext.textfield.fieldMap = {};
yext.textfield.UID = 1;
yext.textfield.UIDString = 'textfieldUID';

/**
 * Model to represent the text field that mirrors
 * TextFieldView.java toJSON
 *
 * @constructor
 * @param {!Object} data Object that should have the same properties as the model
 */
yext.textfield.TextFieldModel = function(data) {
  this.value = data.value || '';
  this.indeterminate = data.indeterminate || false;
  this.displayValue = data.displayValue || data.value || '';
  this.displayIndeterminate = data.displayIndeterminate || false;
  this.assetId = data.assetId || '';
};

/*
 * Returns the json string version of the data that's sent back to the server
 */
yext.textfield.TextFieldModel.prototype.getData = function() {
  return yext.JSON.stringify(this);
};

/**
 * Update the model
 *
 * @param {!Object} data Object that should have the same properties as the model
 */
yext.textfield.TextFieldModel.prototype.update = function(data) {
  if (yext.isString(data.value)) {
    this.value = data.value;
  }
  if (yext.isBoolean(data.indeterminate)) {
    this.indeterminate = data.indeterminate;
  }
  if (yext.isString(data.displayValue)) {
    this.displayValue = data.displayValue;
  }
  if (yext.isBoolean(data.displayIndeterminate)) {
    this.displayIndeterminate = data.displayIndeterminate;
  }
  this.assetId = data.assetId || '';
};

/**
 * @return {yext.textfield.TextFieldModel} Copy of `this`
 */
yext.textfield.TextFieldModel.prototype.copy = function() {
  return new yext.textfield.TextFieldModel(this);
};

/**
 * Text Field class
 *
 * @constructor
 * @param {!jQuery} $field DOM element that wraps the text field
 * @param {string} fieldName The name of the field
 */
yext.textfield.TextField = function($field, fieldName, customFieldId, isSuggestEditMode, viewOnly, usageType) {
  this.$field = $field;

  const _showEmbeddableOptions = $field.data('showEmbeddableOptions')
                                 || $field.find('.js-text-field').data('showEmbeddableOptions');
  this.showEmbeddableOptions = !!_showEmbeddableOptions;
  this.embed = null;
  this.$input = $field.find('.js-text-field-input');
  this.$inputWrap = $field.find('.js-input-wrap');
  this.$inputData = this.$inputWrap.find('.js-input-data');

  this.$chooseTextAsset = $field.find('.js-choose-text-asset');

  this.model = this.buildModel();

  this.isSuggestEditMode = isSuggestEditMode;
  this.viewOnly = viewOnly;
  this.usageType = usageType;

  this.fieldName = fieldName;
  this.customFieldId = customFieldId;

  // Set yext.textfield.UID and store in field map
  this.UID = yext.textfield.UID++;
  this.$field.data(yext.textfield.UIDString, this.UID);
  yext.textfield.fieldMap[this.UID] = this;

  // If field is required
  this.required = this.$input.data('required');

  // Flag for if assets can be used in this field
  this.isAssetField = this.$chooseTextAsset.length;

  // This is for when users only have 'USE_ASSET_FOR_*' permission to edit
  this.useAssetFor = this.$input.is('[readonly]');

  this.setFieldValue();
  this.bindEmbed();
  this.bindAsset();
  this.bindUpdate();
  this.saveValue();

  this.assetOrOriginal = true;
  this.originalValue = this.$inputData.val();
};

/**
 * Set the usage type for a text field
 */
yext.textfield.TextField.prototype.setUsageType = function(usageType) {
  this.usageType = usageType;
};

/**
 * @return {string} The field id
 */
yext.textfield.TextField.prototype.getId = function() {
  if (this.fieldName) {
    return this.fieldName;
  }
  return this.$field.find('[name=field]').val();
};

/**
 * Build the model for the field using its hidden input
 */
yext.textfield.TextField.prototype.buildModel = function() {
  var data = this.parseData(this.$inputData.val());
  // This deals with page navigation where the user may have set some values and then returned
  data.value = this.$input.val();
  return new yext.textfield.TextFieldModel(data);
};

/*
 * Attempts to parse the input div's value into the TextView data model. If undefined, returns
 * an empty model.
 */
yext.textfield.TextField.prototype.parseData = function(val) {
  if (val) {
    return yext.JSON.parse(val);
  } else {
    return {
      value: '',
      displayValue: '',
      indeterminate: false,
      displayIndeterminate: false,
      assetId: '',
    };
  }
};

/**
 * Check to see if the field has embed, if so create embed object
 */
yext.textfield.TextField.prototype.bindEmbed = function() {
  var self = this;
  if (this.showEmbeddableOptions) {
    this.embed = yext.textfield.embed.create(this.$field);
  }

  this.$input.focus(function() {
    self.checkForScrollbar();
  });
};

/**
 * If the field is using text assets, bind text asset events
 */
yext.textfield.TextField.prototype.bindAsset = function() {
  if (this.isAssetField) {
    var self = this;
    yext.textfield.asset.bindSelect(
      this.$field,
      $.proxy(this.assetSelected, this),
      this.fieldName,
      this.customFieldId,
      this.isSuggestEditMode,
      this.usageType,
      this);

    let $clearAssetBox = this.$inputWrap.find('.js-clear-asset');
    $clearAssetBox.on('click', function() {
      self.removeAsset();
      $clearAssetBox.hide();
    });

    if (self.isEmpty()) {
      $clearAssetBox.hide();
    }
  }
};

/**
 * Broadcast when updates are made to this field
 */
yext.textfield.TextField.prototype.bindUpdate = function() {
  var self = this;

  this.$input.inputchange(function() {
    self.set({
      value: self.value(),
      indeterminate: false,
    });
    $(self).trigger('removeasset.textField');
    self.checkForScrollbar();
  });
};

yext.textfield.TextField.prototype.checkForScrollbar = function() {
  if (this.$input.prop('clientHeight') < this.$input.prop('scrollHeight')) {
    this.$input.addClass('scrollbar-is-visible');
  } else {
    this.$input.removeClass('scrollbar-is-visible');
  }
};

/**
 * Reset text field to original state
 */
yext.textfield.TextField.prototype.reset = function() {
  this.set(this.storedModel);
};

/**
 * Clear the field's value
 */
yext.textfield.TextField.prototype.clear = function() {
  this.set({
    value: '',
    indeterminate: false,
  });
};

/**
 * Set the value of the field and do any necessary UI updates
 *
 * @param {TextFieldModel|object} data Model or object that mirrors TextFieldModel
 */
yext.textfield.TextField.prototype.set = function(data) {
  this.model.update(data);
  this.setFieldValue();

  if (this.model.value != this.value()) {
    this.$input.val(this.model.value);
  }

  $(this).trigger('update.textField');
  $(this).trigger('update.struct');
  $(this).trigger('update.list');
  this.updateAssetOrOriginal(false);

  this.checkForScrollbar();
  this.render();
};

/**
 * Set the field value on a hidden input as JSON
 */
yext.textfield.TextField.prototype.setFieldValue = function() {
  this.$inputData.val(this.model.getData());
};

/**
 * Renders the asset provided
 * @param {TextFieldMode.Asset} asset The asset to render
 */
yext.textfield.TextField.prototype.render = function() {
  this.switchDefault();
};

/**
 * Show an hide UI elements to switch to the default mode
 */
yext.textfield.TextField.prototype.switchDefault = function() {
  this.$inputWrap.show();
  this.$chooseTextAsset.show();
};

/**
 * Fired when an asset is selected
 * @param {!Object} asset the asset that was selected
 */
yext.textfield.TextField.prototype.assetSelected = function(asset) {
  this.set({
    assetId: asset.id,
    value: asset.data,
  });
  this.$inputWrap.find('.js-clear-asset').show();
  $(this).trigger('assetselected.textField');
};

/**
 * Clears the value of the field
 */
yext.textfield.TextField.prototype.clearValue = function() {
  this.set({
    assetId: '',
    value: '',
  });
};

yext.textfield.TextField.prototype.removeAsset = function() {
  this.clearValue();
  $(this).trigger('removeasset.textField');
  $(yext.textfield.events).trigger('removeasset.textfield');
};

/** Copy of the one in location/base.js */
yext.textfield.TextField.prototype.emptyFieldMessage = function() {
  if (this.viewOnly) {
    return '<span class="emptyvalue">' + yext.msg('No value') + '</span>';
  } else {
    return '<span class="emptyvalue">' + yext.msg('Click to add') + '</span>';
  }
};

/**
 * Get view mode markup
 * Will escape content by default unless specified otherwise
 *
 * @param {boolean} shouldEscape Indicates whether the field contents should be escaped
 *
 * @return {!jQuery|string}
 */
yext.textfield.TextField.prototype.viewMarkup = function(shouldEscape, cannotEdit) {
  var $markup;

  var displayValue;
  if (shouldEscape === false) {
    displayValue = this.model.displayValue;
  } else {
    displayValue = yext.location.helpers.escapeInput(this.model.displayValue);
  }
  if (this.embed && this.model.displayValue) {
    $markup = this.embed.viewMarkupWithDisplayValues(displayValue);
  } else if (this.model.displayIndeterminate) {
    $markup = yext.location.helpers.variousContentMessage();
  } else {
    $markup = displayValue.replace(/\n/g, '<br>');
  }

  // Empty value case
  if (!$markup) {
    $markup = $(this.emptyFieldMessage(cannotEdit));
  }

  return $markup;
};

/**
 * Get view mode markup that will be a string
 *
 * @return {string}
 */
yext.textfield.TextField.prototype.viewMarkupAsString = function() {
  var markup = this.viewMarkup();
  if (markup instanceof jQuery) {
    return markup[0].outerHTML;
  }
  return markup;
};

/**
 * Returns true if the textfield is empty
 * @return {boolean} true if the markup is empty
 */
yext.textfield.TextField.prototype.isEmpty = function() {
  return (!this.model.value || this.model.value.length == 0) && !this.model.indeterminate;
};

/**
 * @return {boolean} True if the field is indeterminate
 */
yext.textfield.TextField.prototype.isIndeterminate = function() {
  return !!this.model.indeterminate;
};

/**
 * Stored value so it can be restored
 */
yext.textfield.TextField.prototype.saveValue = function() {
  this.storedModel = this.model.copy();
};

/**
 * Shows the text asset selector
 */
yext.textfield.TextField.prototype.assetReplaced = function() {
  yext.dam.assetselector.events.show({
    type: yext.dam.assetselector.types.TEXT,
    includeUsedAssets: true,
    isSuggestEditMode: this.isSuggestEditMode,
    usageType: this.usageType,
  }, this.fieldName, this.customFieldId);
  yext.textfield.asset.bindAssetSelector($.proxy(this.assetSelected, this));
};

/**
 * Call to prevent memory leaks when text field is removed from page
 */
yext.textfield.TextField.prototype.destroy = function() {
  delete yext.textfield.fieldMap[this.UID];
  $(this).off('.textField');
};

/**
 * Get the current value of the field
 */
yext.textfield.TextField.prototype.value = function() {
  return this.$input.val();
};

/**
 * Get the current display value of the field
 */
yext.textfield.TextField.prototype.displayValue = function() {
  return this.model.displayValue;
};

/**
 * Get the asset ID of the field
 */
yext.textfield.TextField.prototype.assetId = function() {
  return this.model.assetId;
};

/**
 * Blur the field
 */
yext.textfield.TextField.prototype.blur = function() {
  this.$input.blur();
};

/**
 * Blur the field
 */
yext.textfield.TextField.prototype.focus = function() {
  this.$input.focus();
};

/**
 * Show the field
 */
yext.textfield.TextField.prototype.show = function() {
  this.$field.show();
};

/**
 * Hide the field
 */
yext.textfield.TextField.prototype.hide = function() {
  this.$field.hide();
};

/**
 * Set the fields placeholder
 *
 * @param {string} placeholder Text for the placeholder
 */
yext.textfield.TextField.prototype.setPlaceholder = function(placeholder) {
  this.$input.attr('placeholder', placeholder);
};

/**
 * Returns a trimmed down version of the model for saving
 *
 * @return {!Object}
 */
yext.textfield.TextField.prototype.coreData = function() {
  return {
    value: this.model.value,
    assetId: this.model.assetId,
    indeterminate: this.model.indeterminate,
  };
};

/**
 * Returns the entire model for saving
 *
 * @return {!Object}
 */
yext.textfield.TextField.prototype.allData = function() {
  return this.model.copy();
};

/**
 * Wraps the allData function for use as a struct child field.
 */
yext.textfield.TextField.prototype.getJSONData = function() {
  return this.allData();
};

/**
 * Apply values to this field from an asset. Also set the assetOrOriginal flag to true.
 */
yext.textfield.TextField.prototype.applyAsset = function(fieldValues) {
  this.set(fieldValues);
  this.updateAssetOrOriginal(true);
};

/**
 * Update the asset or original flag.
 */
yext.textfield.TextField.prototype.updateAssetOrOriginal = function(fromAsset) {
  if (fromAsset || this.originalValue === this.$inputData.val()) {
    this.assetOrOriginal = true;
  } else {
    this.assetOrOriginal = false;
  }
};

/**
 * Type-ubiquitous function for checking assetOrOriginal flag.
 */
yext.textfield.TextField.prototype.checkAssetOrOriginalFlag = function() {
  return this.assetOrOriginal;
};

/**
 * Empty out asset id.
 */
yext.textfield.TextField.prototype.clearAsset = function() {
  this.assetId = '';
};

/**
 * The after save function when this text field is a child of a struct.
 */
yext.textfield.TextField.prototype.setFromStruct = function(fieldValues, $display, $viewTemplate /* unused */, cannotEdit) {
  this.set(fieldValues);
  this.saveValue();
  $display.empty().append(this.viewMarkup(undefined /* shouldEscape */, cannotEdit));

  // reset asset approvals values
  this.assetOrOriginal = true;
  this.originalValue = this.$inputData.val();
};

/**
 * Create a new text field instance. Idempotent
 *
 * @param {!jQuery} $field The dom that wraps the text field
 * @param {string} fieldName The name of the field
 * @param {number} customFieldId The id if it is a custom field, null otherwise
 * @return {yext.textfield.TextField} Instance of yext.textfield.TextField object
 */
yext.textfield.create = function($field, fieldName, customFieldId, isSuggestEditMode, usageType) {
  return yext.textfield.get($field)
    || new yext.textfield.TextField($field, fieldName, customFieldId, isSuggestEditMode, false, usageType);
};

/**
 * Create a new text field instance. Leaves the resulting field's name unset.
 *
 * @param {!jQuery} $field The dom that wraps the text field
 * @param {string} fieldId The id of the field
 * @return {yext.textfield.TextField} Instance of yext.textfield.TextField object
 */
yext.textfield.createWithFieldId = function($field, fieldId) {
  var usageType = goog.module.get('yext.dam.assetselector.usage').forProfileFields(fieldId);
  return yext.textfield.create($field, undefined, undefined, false, usageType);
};

/**
 * Create a new text field instance.
 *
 * @param {!jQuery} $field The dom that wraps the text field
 * @param {string} fieldId The id of the field
 * @return {yext.textfield.TextField} Instance of yext.textfield.TextField object
 */
yext.textfield.createWithUsageType = function($field, fieldId) {
  var usageType = goog.module.get('yext.dam.assetselector.usage').forProfileFields(fieldId);
  return yext.textfield.create($field, fieldId, undefined, false, usageType);
};

/**
 * Get field by element
 *
 * @param {!jQuery} $field The dom that wraps the text field
 * @return {yext.textfield.TextField} Instance of yext.textfield.TextField object
 */
yext.textfield.get = function($field) {
  return yext.textfield.fieldMap[$field.data(yext.textfield.UIDString)];
};

goog.exportSymbol('yext.textfield.events', yext.textfield.events);
